import { runInventory } from '../fs/Walker';
import { publishSnapshot, computeSnapshot } from '../sync/SnapshotProtocol';
import type { InitialSyncResult } from '../types';

import type { BootstrapResult } from './Bootstrap';

export async function runInitialSyncPipeline(
  runtime: BootstrapResult,
  options?: { blockUntilReady?: boolean },
): Promise<InitialSyncResult> {
  const inventoryLogger = runtime.logger.child({ scope: 'inventory' });
  await runInventory({
    rootPath: runtime.config.rootPath,
    bundle: runtime.rules,
    filesRepo: runtime.filesRepo,
    logger: inventoryLogger,
  });

  const hashLogger = runtime.logger.child({ scope: 'hash' });
  let hadWork = true;
  while (hadWork) {
    hadWork = await runtime.hasher.processBatch(runtime.config.hashBatchSize);
    if (hadWork) {
      hashLogger.debug('Hasher processed batch');
    }
  }

  // In blocking mode (default), ensure remote snapshot is READY before returning
  if (options?.blockUntilReady !== false) {
    const syncLogger = runtime.logger.child({ scope: 'snapshot' });
    const result = await publishSnapshot(
      runtime.config.rootPath,
      runtime.filesRepo,
      runtime.snapshotsRepo,
      runtime.clients.sync,
      syncLogger,
      {
        maxAttempts: runtime.config.maxSnapshotAttempts,
        uploadChunkSize: runtime.config.uploadChunkSize,
      },
    );
    return result;
  }

  // Non-blocking mode for continuous service: record snapshot locally and
  // enqueue a job to finish remote processing in the background.
  const computation = computeSnapshot(runtime.filesRepo);
  const createdAt = Date.now();
  runtime.snapshotsRepo.insert(
    computation.snapshotHash,
    computation.filesCount,
    computation.totalSize,
    createdAt,
  );
  runtime.outbox.enqueueSnapshot(runtime.config.rootId, 0);
  return {
    snapshotHash: computation.snapshotHash,
    filesCount: computation.filesCount,
    totalSize: computation.totalSize,
    status: 'READY',
    createdAt,
  };
}
